/**
 * CandidateManagement Component
 *
 * Dashboard page for viewing diary app users and their AI-generated profiles.
 * Shows a table list of all users with a side panel detail view.
 * Handles bilingual fields (_ja / _en) and users without profile data.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Users,
    Search,
    MoreHorizontal,
    X,
    MessageSquare,
    Sparkles,
    Briefcase,
    Home,
    Heart,
    Lightbulb,
    Clock,
    Loader2,
    ArrowLeft,
    ChevronDown,
    ChevronRight,
    FileText,
    Bot,
    User,
    Filter,
    AlertCircle,
    Tag,
} from 'lucide-react';
import {
    diaryUsersService,
    type DiaryUser,
    type DiaryConversation,
} from '@/services/api';
import { useTranslation } from 'react-i18next';

// =============================================================================
// Types
// =============================================================================

interface CandidateManagementProps {
    onNavigateToLogs?: (userId: string) => void;
}

// =============================================================================
// Helpers — bilingual field getters
// =============================================================================

/** Pick JA or EN field based on language, with fallback */
function bi(ja: string | null | undefined, en: string | null | undefined, isJa: boolean): string | null {
    if (isJa) return ja || en || null;
    return en || ja || null;
}

/** Pick JA or EN list field */
function biList(ja: string[] | null | undefined, en: string[] | null | undefined, isJa: boolean): string[] {
    if (isJa) return (ja && ja.length > 0) ? ja : (en || []);
    return (en && en.length > 0) ? en : (ja || []);
}

// =============================================================================
// Main Component
// =============================================================================

export default function CandidateManagement(_props: CandidateManagementProps) {
    const { i18n } = useTranslation();
    const isJa = i18n.language === 'ja';

    const [users, setUsers] = useState<DiaryUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState<DiaryUser | null>(null);
    const [userConversations, setUserConversations] = useState<DiaryConversation[]>([]);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [showDiaryLogs, setShowDiaryLogs] = useState(false);
    const [allConversations, setAllConversations] = useState<DiaryConversation[]>([]);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 50;

    // Debounced search: waits 400ms after user stops typing
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Fetch users
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const result = await diaryUsersService.getAll({
                search: debouncedSearch || undefined,
                limit: PAGE_SIZE,
                offset: page * PAGE_SIZE,
            });
            setUsers(result.users);
            setTotal(result.total);
        } catch (error) {
            console.error('Failed to fetch diary users:', error);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, page]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Debounce the search query
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 400);
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, [searchQuery]);

    // Fetch user detail when selected
    const handleSelectUser = async (user: DiaryUser) => {
        setSelectedUser(user);
        setLoadingDetail(true);
        try {
            const detail = await diaryUsersService.getById(user.id);
            setSelectedUser(detail.user);
            setUserConversations(detail.recentConversations || []);
        } catch (error) {
            console.error('Failed to fetch user detail:', error);
        } finally {
            setLoadingDetail(false);
        }
    };

    // Fetch all conversations for diary logs view
    const handleViewLogs = async () => {
        if (!selectedUser) return;
        setShowDiaryLogs(true);
        setLoadingConversations(true);
        try {
            const result = await diaryUsersService.getConversations(selectedUser.id);
            setAllConversations(result.conversations || []);
        } catch (error) {
            console.error('Failed to fetch conversations:', error);
        } finally {
            setLoadingConversations(false);
        }
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);
    const startIdx = page * PAGE_SIZE + 1;
    const endIdx = Math.min((page + 1) * PAGE_SIZE, total);

    return (
        <div className="flex-1 flex relative overflow-hidden">
            {/* Main Content */}
            <div className="flex-1 p-8 overflow-auto">
                {/* Header */}
                <div className="flex justify-between items-end mb-6">
                    <div>
                        <h2 className="text-lg font-bold text-slate-800">
                            {isJa ? 'ユーザー管理' : 'User Management'}
                        </h2>
                        <p className="text-xs text-slate-500 mt-1">
                            {isJa
                                ? 'AI日記アバターとの会話から生成されたユーザープロファイル'
                                : 'Deep profiles generated from AI avatar diary conversations'}
                        </p>
                    </div>
                </div>

                {/* Search & Filter Bar */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder={isJa ? '名前、メールで検索...' : 'Search by name, email...'}
                            value={searchQuery}
                            onChange={handleSearch}
                            className="pl-9 pr-4 py-2 w-full border border-slate-200 rounded-lg text-sm
                                focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent
                                bg-white placeholder:text-slate-400"
                        />
                    </div>
                    <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                        <Filter size={14} />
                        Filter
                    </button>
                </div>

                {/* Table */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider w-10">
                                    <input type="checkbox" className="rounded border-slate-300" />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    {isJa ? '名前 / メール' : 'Name / Email'}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    {isJa ? '属性' : 'Attributes'}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    {isJa ? '分析タグ' : 'Analysis Tags'}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    {isJa ? '日記ログ' : 'Diary Logs'}
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider w-16">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-12">
                                        <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-slate-800 rounded-full mx-auto mb-2" />
                                        <p className="text-xs text-slate-400">
                                            {isJa ? 'ユーザーを読み込み中...' : 'Loading users...'}
                                        </p>
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-12">
                                        <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm text-slate-400">
                                            {isJa ? 'ユーザーが見つかりません' : 'No users found'}
                                        </p>
                                        <p className="text-xs text-slate-300 mt-1">
                                            {isJa
                                                ? '最初の日記会話の後にユーザーが表示されます'
                                                : 'Users appear here after their first diary conversation'}
                                        </p>
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <UserRow
                                        key={user.id}
                                        user={user}
                                        isSelected={selectedUser?.id === user.id}
                                        onSelect={() => handleSelectUser(user)}
                                        isJa={isJa}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {total > 0 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                            <span className="text-xs text-slate-500">
                                {isJa
                                    ? `${startIdx}–${endIdx} / ${total} 件`
                                    : `Showing ${startIdx}–${endIdx} of ${total}`}
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setPage(Math.max(0, page - 1))}
                                    disabled={page === 0}
                                    className="px-3 py-1 text-xs font-bold text-slate-600 hover:bg-white rounded-lg border border-slate-200
                                        disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isJa ? '前へ' : 'Prev'}
                                </button>
                                <span className="px-2 text-xs text-slate-400">{page + 1} / {totalPages}</span>
                                <button
                                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                                    disabled={page >= totalPages - 1}
                                    className="px-3 py-1 text-xs font-bold text-slate-600 hover:bg-white rounded-lg border border-slate-200
                                        disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isJa ? '次へ' : 'Next'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Side Panel Overlay */}
            {selectedUser && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/30 z-40 animate-fade-in"
                        onClick={() => { setSelectedUser(null); setShowDiaryLogs(false); }}
                    />
                    <UserDetailPanel
                        user={selectedUser}
                        conversations={userConversations}
                        loading={loadingDetail}
                        onClose={() => { setSelectedUser(null); setShowDiaryLogs(false); }}
                        onViewLogs={handleViewLogs}
                        showLogs={showDiaryLogs}
                        onBackToProfile={() => setShowDiaryLogs(false)}
                        allConversations={allConversations}
                        loadingConversations={loadingConversations}
                        isJa={isJa}
                    />
                </>
            )}
        </div>
    );
}

// =============================================================================
// User Table Row
// =============================================================================

function UserRow({
    user,
    isSelected,
    onSelect,
    isJa,
}: {
    user: DiaryUser;
    isSelected: boolean;
    onSelect: () => void;
    isJa: boolean;
}) {
    const initials = (user.displayName || '?')
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();

    const colors = ['bg-indigo-100 text-indigo-700', 'bg-emerald-100 text-emerald-700',
        'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700',
        'bg-sky-100 text-sky-700'];
    const colorIdx = user.displayName.charCodeAt(0) % colors.length;

    const attributes = bi(user.attributesJa, user.attributesEn, isJa);
    const tags = biList(user.analysisTagsJa, user.analysisTagsEn, isJa);

    return (
        <tr
            className={`cursor-pointer transition-colors
                  ${isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
            onClick={onSelect}
        >
            <td className="px-4 py-3">
                <input type="checkbox" className="rounded border-slate-300" onClick={(e) => e.stopPropagation()} />
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${colors[colorIdx]}`}>
                        {initials}
                    </div>
                    <div>
                        <p className="text-sm font-medium text-slate-700">{user.displayName || 'Unknown'}</p>
                        <div className="flex items-center gap-2">
                            <p className="text-xs text-slate-400">{user.email || ''}</p>
                            {!user.hasProfile && (
                                <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-amber-50 text-amber-600 border border-amber-200">
                                    {isJa ? '新規' : 'NEW'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3">
                <span className="text-xs text-slate-600">
                    {attributes || (
                        <span className="text-slate-300 italic">
                            {isJa ? 'データ不足' : 'Not enough data'}
                        </span>
                    )}
                </span>
            </td>
            <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                    {tags.length > 0 ? (
                        tags.slice(0, 3).map((tag, i) => (
                            <span
                                key={i}
                                className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-600"
                            >
                                {tag}
                            </span>
                        ))
                    ) : (
                        <span className="text-xs text-slate-300 italic">—</span>
                    )}
                </div>
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <MessageSquare size={14} className="text-slate-400" />
                    {user.totalConversations || 0}
                </div>
            </td>
            <td className="px-4 py-3 text-right">
                <button
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={(e) => { e.stopPropagation(); onSelect(); }}
                >
                    <MoreHorizontal className="w-4 h-4" />
                </button>
            </td>
        </tr>
    );
}

// =============================================================================
// User Detail Side Panel
// =============================================================================

function UserDetailPanel({
    user,
    conversations,
    loading,
    onClose,
    onViewLogs,
    showLogs,
    onBackToProfile,
    allConversations,
    loadingConversations,
    isJa,
}: {
    user: DiaryUser;
    conversations: DiaryConversation[];
    loading: boolean;
    onClose: () => void;
    onViewLogs: () => void;
    showLogs: boolean;
    onBackToProfile: () => void;
    allConversations: DiaryConversation[];
    loadingConversations: boolean;
    isJa: boolean;
}) {
    const initials = (user.displayName || '?')
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();

    const attributes = bi(user.attributesJa, user.attributesEn, isJa);
    const tags = biList(user.analysisTagsJa, user.analysisTagsEn, isJa);
    const summary = bi(user.aiDiarySummaryJa, user.aiDiarySummaryEn, isJa);
    const recentConversationsSummary = bi(user.recentConversationsSummaryJa, user.recentConversationsSummaryEn, isJa);
    const jobCareer = bi(user.jobCareerJa, user.jobCareerEn, isJa);
    const familyStructure = bi(user.familyStructureJa, user.familyStructureEn, isJa);
    const lifeHistory = bi(user.lifeHistoryJa, user.lifeHistoryEn, isJa);
    const hobbies = biList(user.hobbiesInterestsJa, user.hobbiesInterestsEn, isJa);
    const coreValues = biList(user.coreValuesJa, user.coreValuesEn, isJa);
    const recentTopics = biList(user.recentTopicsJa, user.recentTopicsEn, isJa);
    const noProfileLabel = isJa ? 'データ不足' : 'Not enough data';

    const logsRef = useRef<HTMLDivElement>(null);

    // Scroll to diary logs section when it becomes visible
    useEffect(() => {
        if (showLogs && logsRef.current) {
            logsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [showLogs]);

    return (
        <div className="fixed top-0 right-0 w-[480px] h-full bg-white border-l border-slate-200
                    shadow-2xl overflow-y-auto z-50 animate-slide-in">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 p-5 z-10">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center
                           text-sm font-bold text-slate-600">
                            {initials}
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900">{user.displayName || 'Unknown'}</h2>
                            <p className="text-xs text-slate-400">{attributes || ''}</p>
                            {!user.hasProfile && (
                                <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-50 text-amber-600 border border-amber-200">
                                    {isJa ? '新しいユーザー — プロファイル未生成' : 'New User — Profile not yet generated'}
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Tags */}
                {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                        {tags.map((tag, i) => (
                            <span
                                key={i}
                                className="px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
            ) : !user.hasProfile ? (
                /* New user — no profile yet */
                <div className="p-5 space-y-5">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                            <h3 className="text-sm font-semibold text-amber-800">
                                {isJa ? 'プロファイル未生成' : 'Profile Not Yet Generated'}
                            </h3>
                        </div>
                        <p className="text-sm text-amber-700 leading-relaxed">
                            {isJa
                                ? 'このユーザーはまだ十分な会話データがないため、AIプロファイルが生成されていません。会話が分析された後にプロファイルが表示されます。'
                                : 'This user does not have an AI profile yet. A profile will be generated after their conversations are analyzed.'}
                        </p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-amber-500">
                            <span>📝 {isJa ? `会話数: ${user.totalConversations || 0}` : `Total Diaries: ${user.totalConversations || 0}`}</span>
                        </div>
                    </div>

                    {/* Still show recent conversations if any */}
                    {conversations.length > 0 && (
                        <div>
                            <div className="flex items-center gap-1.5 mb-2">
                                <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                                <h4 className="text-xs font-semibold text-slate-500 uppercase">
                                    {isJa ? '最近の会話' : 'Recent Conversations'}
                                </h4>
                            </div>
                            <div className="space-y-2">
                                {conversations.map((conv) => (
                                    <ConversationCard key={conv.id} conversation={conv} userId={user.id} isJa={isJa} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="p-5 space-y-5">
                    {/* AI Diary Analysis Card */}
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-indigo-600" />
                            <h3 className="text-sm font-semibold text-indigo-900">
                                {isJa ? 'AI日記分析' : 'AI DIARY ANALYSIS'}
                            </h3>
                        </div>
                        <p className="text-sm text-indigo-800 leading-relaxed">
                            {summary || (isJa ? '分析データがまだありません。' : 'No analysis available yet.')}
                        </p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-indigo-500">
                            <span>📝 {isJa ? `会話数: ${user.totalConversations || 0}` : `Total Diaries: ${user.totalConversations || 0}`}</span>
                            <span>{isJa ? '最終更新: ' : 'Last Updated: '}{formatTimestamp(user.lastAnalyzedAt)}</span>
                        </div>
                    </div>

                    {/* Recent Conversations Summary */}
                    {recentConversationsSummary && (
                        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                                <Clock className="w-4 h-4 text-emerald-600" />
                                <h3 className="text-sm font-semibold text-emerald-900">
                                    {isJa ? '最近の会話のまとめ' : 'Recent Conversations Summary'}
                                </h3>
                            </div>
                            <p className="text-sm text-emerald-800 leading-relaxed">
                                {recentConversationsSummary}
                            </p>
                        </div>
                    )}

                    {/* Job & Career */}
                    <ProfileSection
                        icon={<Briefcase className="w-4 h-4 text-slate-500" />}
                        title={isJa ? '職業・キャリア' : 'Job & Career'}
                        content={jobCareer}
                        noDataLabel={noProfileLabel}
                    />

                    {/* Family Structure */}
                    <ProfileSection
                        icon={<Home className="w-4 h-4 text-slate-500" />}
                        title={isJa ? '家族構成' : 'Family Structure'}
                        content={familyStructure}
                        noDataLabel={noProfileLabel}
                    />

                    {/* Hobbies & Interests + Core Values */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <div className="flex items-center gap-1.5 mb-2">
                                <Heart className="w-3.5 h-3.5 text-slate-400" />
                                <h4 className="text-xs font-semibold text-slate-500 uppercase">
                                    {isJa ? '趣味・関心' : 'Hobbies & Interests'}
                                </h4>
                            </div>
                            {hobbies.length > 0 ? (
                                <div className="space-y-1.5">
                                    {hobbies.map((hobby, i) => (
                                        <p key={i} className="text-sm text-slate-700">{hobby}</p>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-slate-400 italic">{noProfileLabel}</p>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-1.5 mb-2">
                                <Lightbulb className="w-3.5 h-3.5 text-slate-400" />
                                <h4 className="text-xs font-semibold text-slate-500 uppercase">
                                    {isJa ? '価値観' : 'Core Values'}
                                </h4>
                            </div>
                            {coreValues.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {coreValues.map((value, i) => (
                                        <span
                                            key={i}
                                            className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700"
                                        >
                                            {value}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-slate-400 italic">{noProfileLabel}</p>
                            )}
                        </div>
                    </div>

                    {/* Life History */}
                    <ProfileSection
                        icon={<Clock className="w-4 h-4 text-slate-500" />}
                        title={isJa ? '人生の経歴' : 'Life History'}
                        content={lifeHistory}
                        noDataLabel={noProfileLabel}
                    />

                    {/* Recent Topics */}
                    {recentTopics.length > 0 && (
                        <div>
                            <div className="flex items-center gap-1.5 mb-2">
                                <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                                <h4 className="text-xs font-semibold text-slate-500 uppercase">
                                    {isJa ? '最近のトピック' : 'Recent Topics'}
                                </h4>
                            </div>
                            <div className="space-y-1.5">
                                {recentTopics.map((topic, i) => (
                                    <div key={i} className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 flex-shrink-0" />
                                        <p className="text-sm text-slate-700">{topic}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Diary Logs View */}
            {showLogs && (
                <div ref={logsRef} className="p-5">
                    <button
                        onClick={onBackToProfile}
                        className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800
                                 font-medium mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {isJa ? 'プロファイルに戻る' : 'Back to Profile'}
                    </button>

                    <div className="flex items-center gap-2 mb-4">
                        <FileText className="w-4 h-4 text-slate-500" />
                        <h3 className="text-sm font-semibold text-slate-900">
                            {isJa ? `日記ログ (${allConversations.length})` : `Diary Logs (${allConversations.length})`}
                        </h3>
                    </div>

                    {loadingConversations ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
                        </div>
                    ) : allConversations.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-8">
                            {isJa ? 'まだ日記の会話がありません。' : 'No diary conversations yet.'}
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {allConversations.map((conv) => (
                                <ConversationCard key={conv.id} conversation={conv} userId={user.id} isJa={isJa} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex gap-3">
                {showLogs ? (
                    <button
                        onClick={onBackToProfile}
                        className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium
                         text-slate-700 hover:bg-slate-50 transition-colors text-center"
                    >
                        {isJa ? '← プロファイルに戻る' : '← Back to Profile'}
                    </button>
                ) : (
                    <button
                        onClick={onViewLogs}
                        className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium
                         text-slate-700 hover:bg-slate-50 transition-colors text-center"
                    >
                        {isJa ? '日記ログを表示' : 'View Diary Logs'}
                    </button>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// Helpers
// =============================================================================

function ProfileSection({
    icon,
    title,
    content,
    noDataLabel,
}: {
    icon: React.ReactNode;
    title: string;
    content: string | null | undefined;
    noDataLabel?: string;
}) {
    return (
        <div>
            <div className="flex items-center gap-1.5 mb-2">
                {icon}
                <h4 className="text-xs font-semibold text-slate-500 uppercase">{title}</h4>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
                {content || <span className="text-slate-400 italic">{noDataLabel || 'Not yet disclosed'}</span>}
            </p>
        </div>
    );
}

function formatTimestamp(ts: unknown): string {
    if (!ts) return '';
    try {
        if (typeof ts === 'object' && ts !== null && 'seconds' in ts) {
            const d = new Date((ts as { seconds: number }).seconds * 1000);
            return d.toLocaleDateString('ja-JP');
        }
        if (typeof ts === 'string') {
            return new Date(ts).toLocaleDateString('ja-JP');
        }
        return '';
    } catch {
        return '';
    }
}

// =============================================================================
// Conversation Card (expandable) — bilingual
// =============================================================================

function ConversationCard({ conversation, userId, isJa }: { conversation: DiaryConversation; userId: string; isJa: boolean }) {
    const [expanded, setExpanded] = useState(false);
    const [fullData, setFullData] = useState<Record<string, unknown> | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const title = bi(conversation.titleJa, conversation.titleEn, isJa) || conversation.title;
    const summary = bi(conversation.summaryJa, conversation.summaryEn, isJa);
    const themes = biList(conversation.themesJa, conversation.themesEn, isJa);
    const keyPoints = biList(conversation.keyPointsJa, conversation.keyPointsEn, isJa);

    const handleExpand = async () => {
        if (expanded) {
            setExpanded(false);
            return;
        }
        setExpanded(true);
        if (fullData) return;
        setLoadingDetail(true);
        try {
            const result = await diaryUsersService.getConversation(userId, conversation.id);
            setFullData(result.conversation);
        } catch (error) {
            console.error('Failed to fetch conversation:', error);
        } finally {
            setLoadingDetail(false);
        }
    };

    const hasAnalysis = summary || themes.length > 0 || keyPoints.length > 0;

    return (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            {/* Header — always visible */}
            <button
                onClick={handleExpand}
                className="w-full flex items-start gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
            >
                <div className="flex-shrink-0 mt-0.5">
                    {expanded
                        ? <ChevronDown className="w-4 h-4 text-slate-400" />
                        : <ChevronRight className="w-4 h-4 text-slate-400" />
                    }
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                        {title || 'Untitled entry'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                        {formatTimestamp(conversation.createdAt)}
                        {conversation.status && ` · ${conversation.status}`}
                        {conversation.mood && ` · ${conversation.mood}`}
                    </p>
                    {summary && (
                        <p className="text-xs text-slate-500 mt-1.5 line-clamp-2 leading-relaxed">
                            {summary}
                        </p>
                    )}
                </div>
            </button>

            {/* Expanded — Analysis + Transcript */}
            {expanded && (
                <div className="border-t border-slate-100">
                    {/* AI Analysis Section */}
                    {hasAnalysis && (
                        <div className="bg-gradient-to-br from-indigo-50/50 to-purple-50/50 px-4 py-3 space-y-3">
                            <div className="flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                                <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                                    {isJa ? 'AI分析' : 'AI Analysis'}
                                </span>
                            </div>

                            {summary && (
                                <div>
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">
                                        {isJa ? 'まとめ' : 'Summary'}
                                    </p>
                                    <p className="text-xs text-slate-700 leading-relaxed">{summary}</p>
                                </div>
                            )}

                            {keyPoints.length > 0 && (
                                <div>
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">
                                        {isJa ? 'ポイント' : 'Key Points'}
                                    </p>
                                    <ul className="space-y-1">
                                        {keyPoints.map((point, i) => (
                                            <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                                                <span className="text-indigo-400">•</span>
                                                {point}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {themes.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {themes.map((theme, i) => (
                                        <span key={i} className="px-2 py-0.5 text-[10px] font-bold rounded bg-indigo-100 text-indigo-600 flex items-center gap-1">
                                            <Tag className="w-2.5 h-2.5" />
                                            {theme}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Transcript Section */}
                    <div className="bg-slate-50 px-4 py-3">
                        {loadingDetail ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                            </div>
                        ) : conversation.fullTranscript ? (
                            <div className="space-y-2.5 max-h-[300px] overflow-y-auto">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">
                                    {isJa ? '会話の記録' : 'Transcript'}
                                </p>
                                {parseTranscript(conversation.fullTranscript).map((msg, i) => (
                                    <div key={i} className="flex items-start gap-2">
                                        <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center
                                            ${msg.role === 'assistant'
                                                ? 'bg-indigo-100 text-indigo-600'
                                                : 'bg-slate-200 text-slate-500'}`}
                                        >
                                            {msg.role === 'assistant'
                                                ? <Bot className="w-3 h-3" />
                                                : <User className="w-3 h-3" />
                                            }
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-slate-700 leading-relaxed">{msg.content}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 text-center py-4">
                                {isJa ? '記録がありません。' : 'No transcript recorded.'}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/** Parse "Assistant: text\nUser: text" transcript format */
function parseTranscript(transcript: string): Array<{ role: string; content: string }> {
    const messages: Array<{ role: string; content: string }> = [];
    const lines = transcript.split('\n');
    let currentRole = '';
    let currentContent = '';

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        if (/^assistant:/i.test(trimmed)) {
            if (currentRole && currentContent) {
                messages.push({ role: currentRole, content: currentContent.trim() });
            }
            currentRole = 'assistant';
            currentContent = trimmed.replace(/^assistant:\s*/i, '');
        } else if (/^user:/i.test(trimmed)) {
            if (currentRole && currentContent) {
                messages.push({ role: currentRole, content: currentContent.trim() });
            }
            currentRole = 'user';
            currentContent = trimmed.replace(/^user:\s*/i, '');
        } else {
            currentContent += ' ' + trimmed;
        }
    }

    if (currentRole && currentContent) {
        messages.push({ role: currentRole, content: currentContent.trim() });
    }

    return messages;
}
